import React, { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiCheck } from 'react-icons/bi';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import ProBadge from '../../components/ui/ProBadge';
import { BotTypeEnum } from '../../utils/BotTypeEnum';
import { handleSetPlugAndPlayInfos, wizardState } from './botSlice';
import InfoMessageAlert from './InfoMessageAlert';
import { AIKittyType } from './types';

type Props = {
  formBuilder: UseFormReturn<any, any>;
};
const PlugAndPlayAutoUpdateEdit = ({ formBuilder }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    watch,
    setValue,
    control,
    formState: { errors },
    trigger,
  } = formBuilder;

  const wizard = useAppSelector(wizardState);
  const plugAndPlayPermissions = useAppSelector(state => (state.dashboardState as any).Client.botConfigs).find(
    (item: any) => item.botName === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY
  )?.config;

  const autoUpdateRules: Array<{
    title: string;
    id: string;
    description: string;
  }> = [
    {
      id: 'never',
      title: t('Wizard/PlugAndPlayInfos/key5'),
      description: t('Wizard/PlugAndPlayInfos/key6'),
    },
    {
      id: 'weekly',
      title: t('Wizard/PlugAndPlayInfos/key7'),
      description: t('Wizard/PlugAndPlayInfos/key8'),
    },
    {
      id: 'daily',
      title: t('Wizard/PlugAndPlayInfos/key9'),
      description: t('Wizard/PlugAndPlayInfos/key10'),
    },
  ];

  const handleUpdate = () => {
    dispatch(
      handleSetPlugAndPlayInfos({
        auto_update: watch('auto_update'),
      })
    );
  };

  // TODO => validation logic via permissions
  const hasPermission = (type: string) => {
    return type === 'never' ? true : plugAndPlayPermissions?.allowed_schedule?.includes(type);
  };

  useEffect(() => {
    if (wizard.bot_config && (wizard.bot_config as AIKittyType).plug_and_play?.auto_update) {
      setValue('auto_update', (wizard.bot_config as AIKittyType).plug_and_play?.auto_update);
    }
    return () => {
      setValue('auto_update', '');
    };
  }, [wizard.bot_config]);

  return (
    <div className="plug-and-play tw-py-4">
      <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-inline-flex">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            4. {t('Wizard/PlugAndPlayInfos/key3')}
          </div>
          <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('Wizard/PlugAndPlayInfos/key4')}
          </div>
        </div>

        <InfoMessageAlert texts={[t('Wizard/PlugAndPlayAlert/key1')]} />

        <div className="tw-self-stretch tw-min-h-[104px] tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-5 tw-flex  md:tw-flex-row tw-flex-col">
            {autoUpdateRules.map((rule, index) => (
              <Controller
                key={index}
                control={control}
                name="address"
                render={() => (
                  <div
                    className={`tw-border tw-border-solid  tw-relative tw-h-[132px] md:tw-max-w-[251.2px] tw-w-full tw-px-4 tw-pt-4 tw-pb-6 tw-bg-white tw-rounded-2xl tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex ${
                      (wizard.bot_config as AIKittyType).plug_and_play?.auto_update === rule.id ? ' tw-border-blue-500' : ' tw-border-gray-200'
                    } ${hasPermission(rule.id) ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed'}`}
                    role="button"
                    id={`btn-plug-play-rule-${rule.id}`}
                    onClick={() => {
                      if (hasPermission(rule.id)) {
                        setValue('auto_update', rule.id, { shouldDirty: true });
                        trigger('auto_update');
                        handleUpdate();
                      }
                    }}
                    onKeyDown={() => {
                      if (hasPermission(rule.id)) {
                        setValue('auto_update', rule.id, { shouldDirty: true });
                        trigger('auto_update');
                        handleUpdate();
                      }
                    }}
                    tabIndex={0}
                  >
                    <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                      {(wizard.bot_config as AIKittyType).plug_and_play?.auto_update === rule.id ? (
                        <div className="tw-w-4 tw-h-4 tw-bg-primary tw-rounded tw-border tw-border-blue-600 tw-justify-center tw-items-center tw-flex">
                          <BiCheck className="tw-text-white" size={16} />
                        </div>
                      ) : (
                        <div
                          className={`tw-w-4 tw-h-4 tw-relative ${
                            !hasPermission(rule.id) ? 'tw-bg-gray-200' : 'tw-bg-white '
                          } tw-rounded tw-border tw-border-solid tw-border-gray-300`}
                        />
                      )}

                      {!hasPermission(rule.id) && <ProBadge variant="large" />}
                    </div>
                    <div className="tw-self-stretch tw-h-16 tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                      <div className="tw-self-stretch tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{rule.title}</div>
                      <div className="tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{rule.description}</div>
                    </div>
                  </div>
                )}
              />
            ))}
          </div>
          {!!errors.auto_update && <span className="tw-text-sm tw-text-red-500 tw-block">{errors?.auto_update?.message as string}</span>}
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(PlugAndPlayAutoUpdateEdit);
